import {
  LEVIER_BORDER_CLASSES,
  LEVIER_SOLID_CLASSES,
  LEVIER_TINT_CLASSES,
  cellPaddingClassFor,
  headerBackgroundClassFor,
  headerSelectionRingClassFor,
  headerStyleFor,
  levierColorTokenFor,
  resolveLevierColorTreatment,
} from '@/lib/display-tweaks';
import { targetKey } from '@/lib/notes-queries';
import { useTweakByKey } from '@/lib/tweak-store';
import { cn } from '@/lib/utils';
import {
  type Cell,
  type CellRowType,
  type Dimension,
  type DimensionGenerationJob,
  type MatrixViewStateShape,
  type NoteTarget,
  type Noyau,
  type NoyauColorToken,
  PALIER_NUMBERS,
  type ResolvedPalier,
  type SingularityLevier,
  palierPosition,
  parseBulletLines,
} from '@mss_creator/shared';
import { Button, Icon, Tooltip } from '@pms-digital/design-system';
import { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent,
  ReactNode,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { AddDimensionButton } from './add-dimension-button';
import {
  type ClippedEdges,
  NO_CLIPPED_EDGES,
  clippedEdgeClasses,
  clippedEdges,
  sameClippedEdges,
} from './clipped-edges';
import { DrawerMenuPanel } from './drawer-menu-panel';
import { computeFitScale } from './global-view';
import { MatrixCellPreview } from './matrix-cell-preview';
import { ACTIVATION_ROW_COLLAPSED_HINT, ACTIVATION_ROW_LABEL } from './matrix-labels';
import { NoteAddButton } from './note-add-button';
import { NoteMarker, noteAnchorOf } from './note-marker';
import type { GridNotes } from './note-targets';
import { type DrawerRequest, type Selection, toggleCellInSelection } from './selection';
import { ZOOM_TEXT_CLASSES } from './zoom';

/**
 * Global view pins one base text size instead of honouring
 * `viewState.zoomStep` (2026-08-25 design doc, V8). A larger base size only
 * yields a proportionally smaller fit factor, so the apparent result is
 * identical — but honouring a *shared, server-persisted* setting would make the
 * factor differ between two viewers of the same matrix for no visible gain, and
 * would stop two people on a screen share seeing the same thing.
 */
const GLOBAL_VIEW_TEXT_CLASS = ZOOM_TEXT_CLASSES[1];

/**
 * Accessible name *and* hover tooltip of the D25 per-cell staleness marker.
 * One constant because the two must never diverge — a mouse user and a screen
 * reader user have to be told the same thing about the same dot.
 */
const CONTEXT_STALE_MARKER_LABEL = 'Cellule antérieure à la dernière modification du contexte';

export interface MatrixGridProps {
  cells: Cell[];
  dimensions: Dimension[];
  singularitySetup: SingularityLevier[];
  paliers: ResolvedPalier[];
  /**
   * The company's noyau. Only used to colour the activation rows per levier
   * when the `matrix-activation-levier-colors` tweak is on — the mapping is
   * `NOYAU_STEP_ORDER[noyau][levier.order - 1]`, exactly as the companies
   * page's own singularity-setup table derives it.
   */
  noyau: Noyau;
  viewState: MatrixViewStateShape;
  /**
   * Global (fit-to-screen) view mode — the whole visible matrix is scaled to
   * fit the frame and the grid becomes read-only (2026-08-25 design doc).
   * Owned by the route; per-viewer, never part of `viewState`.
   */
  globalView: boolean;
  selection: Selection;
  onSelect: (selection: Selection) => void;
  editingCellId: string | null;
  onStartEdit: (cellId: string) => void;
  onSaveEdit: (cellId: string, content: string) => void;
  /** Escape while editing (point 9) — discard changes and leave edit mode without saving. */
  onCancelEdit: (cellId: string) => void;
  onResize: (target: 'column' | 'row', ref: string, sizePx: number) => void;
  onToggleVisibility: (
    target: 'activation' | 'column' | 'dimension',
    ref: string | null,
    hidden: boolean,
  ) => void;
  /** "..." menu → open the drawer on this scope + tab (M7). */
  onDrawerRequest: (request: DrawerRequest) => void;
  /** Multi-select mode (M11) — owned by the route, toggled from the toolbar. */
  multiSelectMode: boolean;
  /**
   * Regenerated-but-not-currently-selected cells (2026-08-14 design doc, I7/I8)
   * — a small corner badge on each, cleared on hover via `onDismissRegenerationBadge`.
   * Ephemeral, per-viewer, owned by the route.
   */
  recentlyRegeneratedCellIds: ReadonlySet<string>;
  onDismissRegenerationBadge: (cellId: string) => void;
  /** Cell ids whose current version predates `matrix.contextUpdatedAt` (D25). Empty once the banner is dismissed. */
  staleCellIds?: readonly string[];
  /**
   * The latest add-dimension job (point 14), or `null` when none has ever
   * run. Drives the pending-cell skeleton / failed-row retry state on
   * whichever dimension row matches `job.dimensionId` — the dimension row
   * itself already exists (created eagerly by the backend before the job
   * even starts), only its 8 cells are still missing.
   */
  dimensionJob: DimensionGenerationJob | null;
  onAddDimension: (input: { title: string; comment: string }) => void;
  /** True while the `POST .../dimensions/generation-jobs` request itself is in flight. */
  isAddingDimension: boolean;
  /**
   * Short French message when the `POST .../dimensions/generation-jobs`
   * request itself failed (transport/server error, not a validation one —
   * see `AddDimensionButtonProps.submitError`). `null` when the last attempt
   * (if any) didn't error.
   */
  addDimensionError: string | null;
  onRetryDimensionJob: (jobId: string) => void;
  /** True while the `POST .../generation-jobs/:id/retry` request itself is in flight. */
  isRetryingDimensionJob: boolean;
  /**
   * The notes of the matrix (issue #19): markers on the cells and headers, the
   * « + » bubble, « Ajouter une note » in the « ⋯ » menus. Absent, none of it.
   */
  notes?: GridNotes;
}

/** A row that actually carries selectable cells (an activation sub-row or a visible dimension). */
interface BodyRow {
  kind: 'body';
  rowType: CellRowType;
  rowRef: string;
  label: string;
  /**
   * True only for the first of the 6 activation sub-rows. It carries the
   * "Ligne d'activation" group label and the block's single collapse control
   * (M3 + M6: the 6 sub-rows collapse together, never individually).
   */
  isActivationHead: boolean;
}

/** The slim gray band replacing the whole activation block while collapsed (M15). */
interface CollapsedActivationRow {
  kind: 'activation-collapsed';
}

/** The slim gray band a hidden dimension row shrinks to, still re-expandable (M5). */
interface CollapsedDimensionRow {
  kind: 'dimension-collapsed';
  rowRef: string;
  label: string;
}

type RenderedRow = BodyRow | CollapsedActivationRow | CollapsedDimensionRow;

const DEFAULT_COLUMN_WIDTH_PX = 224;
const MIN_COLUMN_WIDTH_PX = 80;
const MAX_COLUMN_WIDTH_PX = 600;

const DEFAULT_ROW_HEIGHT_PX = 64;
const MIN_ROW_HEIGHT_PX = 40;
const MAX_ROW_HEIGHT_PX = 400;

type DragTarget =
  | { type: 'column'; ref: string; startPos: number; startSize: number }
  | { type: 'row'; ref: string; startPos: number; startSize: number };

/**
 * Shared chrome for the sticky label column(s): the corner cell, every row
 * header, and every collapsed-row band — 200px wide, warm-gray, with the
 * mockup's heavier right divider separating the block from the scrolling body.
 *
 * There is normally one such column. The `matrix-activation-levier-names`
 * tweak can add a *second* one for the per-levier names, so these pieces are
 * composed rather than baked into a single constant: the heavy divider then
 * belongs to the last label column only, and the second column's `left` offset
 * has to equal the first one's width or sticky positioning will overlap/gap.
 */

/**
 * Everything a label column shares regardless of position/width. `sticky`
 * itself is applied separately (see `labelColumnPositionClass`): in global view
 * the table is `transform`-scaled, which creates a containing block that breaks
 * sticky against the scroll box — and nothing scrolls in that mode anyway, so
 * there is nothing to pin (2026-08-25 design doc, V2).
 */
const LABEL_COLUMN_SHELL = 'z-10 border-gray-100 border-b';
/** The heavy divider that separates the label block from the scrolling body — last column only. */
const LABEL_COLUMN_OUTER_DIVIDER = 'border-r-2 border-r-gray-200';
/** The light rule between two label columns. */
const LABEL_COLUMN_INNER_DIVIDER = 'border-r border-r-gray-100';

/**
 * Width of the group (first) label column, and the matching `left` offset for
 * the name column that follows it. These MUST stay in lockstep: the second
 * column is `position: sticky`, so its offset is what pins it flush against
 * the first rather than overlapping or gapping.
 */
const GROUP_COLUMN_WIDE = 'left-0 w-50 min-w-50';
const GROUP_COLUMN_STRIP = 'left-0 w-7 min-w-7';
const NAME_COLUMN_AFTER_WIDE = 'left-32 w-50 min-w-50';
const NAME_COLUMN_AFTER_STRIP = 'left-6 w-50 min-w-50';

/** Hover/focus-revealed control (collapse arrows + "..." menus). Never `display:none` — it stays keyboard-reachable. */
const HOVER_CONTROL_CLASS =
  'flex-shrink-0 rounded p-0 text-gray-600 opacity-0 transition-opacity hover:bg-gray-200 focus-visible:opacity-100 group-hover:opacity-100 aria-expanded:opacity-100';

/** Activates `action` on Enter/Space, mirroring the element's `onClick`, so keyboard-only
 * navigation can select headers/cells too (not just mouse). */
function handleActivationKey(event: KeyboardEvent, action: () => void): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    action();
  }
}

/**
 * Puts the caret at the very end of `node`'s text and focuses it. Used both
 * when an editor first mounts and when a click lands in the cell's padding
 * below the text (point 4) — there the whole cell acts as one text surface, so
 * the click has to land *in* the editor rather than on the <td> behind it.
 *
 * Caret placement is a UX nicety, so environments with an incomplete
 * Selection/Range implementation (jsdom) are tolerated rather than thrown on.
 */
function focusWithCaretAtEnd(node: HTMLElement): void {
  node.focus();
  try {
    const range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(false);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
  } catch {
    // Selection/Range unavailable — focus alone is enough.
  }
}

function currentContent(cell: Cell | undefined): string {
  if (!cell || cell.currentVersionId === null) return '';
  const version = cell.versions.find((entry) => entry.id === cell.currentVersionId);
  return version?.content ?? '';
}

function rowHeightKey(rowType: CellRowType, rowRef: string): string {
  return `${rowType}:${rowRef}`;
}

/**
 * Splices `text` in at the caret as a single plain-text node, replacing the
 * current selection — the paste path for the contentEditable cell editor.
 *
 * `contentEditable="plaintext-only"` already normalizes a rich paste natively
 * in Chrome/Edge/Safari; Firefox has historically ignored that value, and
 * there a rich or multi-line paste inserts real block elements (a `<div>` or
 * `<p>` per line). `textContent` concatenates across element boundaries
 * *without* adding a newline, so on save that block would collapse into one
 * run-on line, merging away every `"- "` bullet marker `parseBulletLines`
 * depends on. Hence the belt-and-braces interception.
 *
 * `document.execCommand('insertText')` is nominally deprecated, but it is the
 * only insertion path that participates in the browser's native undo stack —
 * which matters here, since Ctrl+Z inside the editor must undo typing rather
 * than the last matrix action (see `keyboard-shortcuts.ts`). It is therefore
 * preferred, with a Selection/Range splice as the fallback for environments
 * that don't implement it (jsdom included).
 */
function insertPlainTextAtCaret(text: string): void {
  try {
    if (
      typeof document.execCommand === 'function' &&
      document.execCommand('insertText', false, text)
    ) {
      return;
    }
  } catch {
    // Some engines throw instead of returning false; fall through to the
    // Selection/Range splice below.
  }
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/**
 * The 8-palier-column × N-row generation grid (6 activation-des-leviers
 * sub-rows + one row per matrix dimension). Sticky header row + sticky first
 * column. Selection and inline-edit state are fully owned by the parent route
 * — this component only reports intent (`onSelect`/`onStartEdit`/`onSaveEdit`/
 * `onResize`/`onToggleVisibility`/`onDrawerRequest`).
 *
 * Structure follows the 2026-08-06 design doc's session-4 decisions: the
 * corner header cell is empty and "Ligne d'activation" is a body-row label
 * (M3); collapsing hides nothing from the DOM, it shrinks the column/row to a
 * re-expandable band (M5/M15); the "..." menu is a drawer shortcut, not a hide
 * menu (M4/M7); and multi-select replaces click-to-edit while active (M11).
 */
export const MatrixGrid = forwardRef<HTMLDivElement, MatrixGridProps>(function MatrixGrid(
  {
    cells,
    dimensions,
    singularitySetup,
    paliers,
    noyau,
    viewState,
    globalView,
    selection,
    onSelect,
    editingCellId,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onResize,
    onToggleVisibility,
    onDrawerRequest,
    multiSelectMode,
    recentlyRegeneratedCellIds,
    onDismissRegenerationBadge,
    staleCellIds,
    dimensionJob,
    onAddDimension,
    isAddingDimension,
    addDimensionError,
    onRetryDimensionJob,
    isRetryingDimensionJob,
    notes,
  },
  ref,
) {
  // ── Cell padding tweak (point 6) ────────────────────────────────────────────
  const cellPaddingTweak = useTweakByKey('matrix-cell-padding') ?? 'compact';
  const cellPaddingClass = cellPaddingClassFor(String(cellPaddingTweak));

  // ── Activation levier names + colors (2026-08-24 grill, point 7) ────────────
  const levierNamesTweak = String(useTweakByKey('matrix-activation-levier-names') ?? 'hidden');
  const levierColorsTweak = String(useTweakByKey('matrix-activation-levier-colors') ?? 'off');

  /** `column`/`inline` add a second sticky label column for the levier names. */
  const hasNameColumn = levierNamesTweak !== 'hidden';
  /** `inline` shrinks the group column to a control strip; `column` keeps it full width. */
  const isStripMode = levierNamesTweak === 'inline';
  const labelColumnCount = hasNameColumn ? 2 : 1;

  /**
   * `sticky` in normal view, nothing in global view — see `LABEL_COLUMN_SHELL`.
   * The `left-*` offsets stay either way: they are inert on a static element,
   * and keeping them means one branch instead of three.
   */
  const labelColumnPositionClass = globalView ? null : 'sticky';
  /** Same, for the two sticky header rows in `<thead>`. */
  const headerRowPositionClass = globalView ? null : 'sticky top-0';

  const groupColumnClass = cn(
    LABEL_COLUMN_SHELL,
    labelColumnPositionClass,
    isStripMode ? GROUP_COLUMN_STRIP : GROUP_COLUMN_WIDE,
    hasNameColumn ? LABEL_COLUMN_INNER_DIVIDER : LABEL_COLUMN_OUTER_DIVIDER,
  );
  const nameColumnClass = cn(
    LABEL_COLUMN_SHELL,
    labelColumnPositionClass,
    isStripMode ? NAME_COLUMN_AFTER_STRIP : NAME_COLUMN_AFTER_WIDE,
    LABEL_COLUMN_OUTER_DIVIDER,
  );
  /** For cells that span the whole label block (corner, dimension rows, collapsed bands). */
  const spanningLabelColumnClass = cn(
    LABEL_COLUMN_SHELL,
    labelColumnPositionClass,
    'left-0',
    LABEL_COLUMN_OUTER_DIVIDER,
    hasNameColumn ? null : 'w-50 min-w-50',
  );

  /**
   * With no per-levier header cell there is nothing to put a border or a tint
   * on, so `accent`/`tint` degrade to the six-segment band rather than becoming
   * a settings control that appears to do nothing (grill decision).
   */
  const levierColorTreatment = resolveLevierColorTreatment(levierColorsTweak, levierNamesTweak);

  /** The colour token for the levier at `order` (1-6), via the company's noyau step order. */
  function levierColorToken(order: number): NoyauColorToken {
    return levierColorTokenFor(noyau, order);
  }

  // ── Column header color theme ───────────────────────────────────────────────
  // Three themes: 'white' (flat), 'pitch' (one token per palier, from the
  // project's original pitch doc), 'noyau-gradient' (white → the company's real
  // noyau colour across paliers 0..6). Each owns its own selection treatment —
  // there is no separate "selection matches theme" toggle any more.
  const headerThemeTweak = useTweakByKey('matrix-header-theme') ?? 'pitch';
  const isGradientTheme = headerThemeTweak === 'noyau-gradient';

  function headerBgClass(columnNumber: number): string {
    return headerBackgroundClassFor(String(headerThemeTweak), columnNumber);
  }

  /**
   * Inline background / text colour for one header. Text colour is set here on
   * *every* theme (not just the gradient) so the palier label inherits it —
   * replacing the old `text-gray-500` label, which failed
   * WCAG AA at 3.45:1 on plain white.
   */
  function headerStyle(columnNumber: number): CSSProperties {
    return headerStyleFor(String(headerThemeTweak), noyau, columnNumber);
  }

  function headerSelectionRingClass(columnNumber: number): string {
    return headerSelectionRingClassFor(String(headerThemeTweak), columnNumber);
  }

  // ── Drag-to-resize (E8) ────────────────────────────────────────────────────
  // `dragRef` holds the in-flight drag's math (not reactive — updating it on
  // every pointermove would re-run the effect below on every pixel). `liveSize`
  // is the reactive live-preview value used for rendering only; the parent's
  // `onResize` is called exactly once, on pointerup.
  const dragRef = useRef<DragTarget | null>(null);
  const [liveSize, setLiveSize] = useState<{
    type: 'column' | 'row';
    ref: string;
    px: number;
  } | null>(null);

  useEffect(() => {
    function handleMove(event: PointerEvent) {
      const drag = dragRef.current;
      if (!drag) return;
      const delta =
        drag.type === 'column' ? event.clientX - drag.startPos : event.clientY - drag.startPos;
      const [min, max] =
        drag.type === 'column'
          ? [MIN_COLUMN_WIDTH_PX, MAX_COLUMN_WIDTH_PX]
          : [MIN_ROW_HEIGHT_PX, MAX_ROW_HEIGHT_PX];
      const next = Math.min(max, Math.max(min, drag.startSize + delta));
      setLiveSize({ type: drag.type, ref: drag.ref, px: next });
    }
    function handleUp() {
      const drag = dragRef.current;
      if (!drag) return;
      setLiveSize((current) => {
        if (current) onResize(current.type, current.ref, Math.round(current.px));
        return null;
      });
      dragRef.current = null;
    }
    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
    };
  }, [onResize]);

  function beginColumnResize(event: ReactPointerEvent, columnRef: string, startSize: number) {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { type: 'column', ref: columnRef, startPos: event.clientX, startSize };
    setLiveSize({ type: 'column', ref: columnRef, px: startSize });
  }

  function beginRowResize(event: ReactPointerEvent, rowRef: string, startSize: number) {
    event.preventDefault();
    event.stopPropagation();
    dragRef.current = { type: 'row', ref: rowRef, startPos: event.clientY, startSize };
    setLiveSize({ type: 'row', ref: rowRef, px: startSize });
  }

  function columnWidthPx(columnNumber: number): number {
    const columnRef = String(columnNumber);
    if (liveSize?.type === 'column' && liveSize.ref === columnRef) return liveSize.px;
    return viewState.columnWidths[columnRef] ?? DEFAULT_COLUMN_WIDTH_PX;
  }

  function rowHeightPx(rowType: CellRowType, rowRef: string): number {
    const key = rowHeightKey(rowType, rowRef);
    if (liveSize?.type === 'row' && liveSize.ref === key) return liveSize.px;
    return viewState.rowHeights[key] ?? DEFAULT_ROW_HEIGHT_PX;
  }

  // ── "..." drawer-shortcut menu (M7) ────────────────────────────────────────
  // Deliberately local state rather than a new UI primitive: only one menu is
  // ever open, so a single key + the button that opened it is enough. The panel
  // itself is a portal (issue #23, `DrawerMenuPanel`), which closes it on Escape,
  // a click elsewhere, a scroll and a resize.
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [openMenuAnchor, setOpenMenuAnchor] = useState<HTMLElement | null>(null);

  function closeMenu() {
    setOpenMenuKey(null);
    setOpenMenuAnchor(null);
  }

  function renderDrawerMenu(
    menuKey: string,
    triggerLabel: string,
    menuSelection: Selection,
    align: 'left' | 'right',
    noteTarget: NoteTarget | null = null,
  ): ReactNode {
    // Global view has no drawer to open (design doc F2/R4), so the shortcut to
    // it is dropped at the source rather than guarded at all six call sites.
    if (globalView) return null;
    const isOpen = openMenuKey === menuKey;
    function request(tab: DrawerRequest['tab']) {
      closeMenu();
      onDrawerRequest({ selection: menuSelection, tab });
    }
    return (
      <>
        <button
          type="button"
          aria-label={triggerLabel}
          aria-haspopup="menu"
          aria-expanded={isOpen}
          onClick={(event) => {
            event.stopPropagation();
            if (isOpen) {
              closeMenu();
              return;
            }
            setOpenMenuKey(menuKey);
            setOpenMenuAnchor(event.currentTarget);
          }}
          className={HOVER_CONTROL_CLASS}
        >
          <Icon name="more_horiz" size={16} />
        </button>
        {isOpen && openMenuAnchor && (
          <DrawerMenuPanel anchor={openMenuAnchor} align={align} onClose={closeMenu}>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                request('regen');
              }}
              className="flex w-full items-center gap-2 whitespace-nowrap px-3 py-2 text-left font-semibold text-[0.9em] hover:bg-gray-50" // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
            >
              Régénérer avec commentaire
            </button>
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                request('historique');
              }}
              className="flex w-full items-center gap-2 whitespace-nowrap border-gray-100 border-t px-3 py-2 text-left font-semibold text-[0.9em] hover:bg-gray-50" // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
            >
              Historique
            </button>
            {notes && noteTarget && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  // The panel sits outside the table: the note anchors on the cell of the button.
                  const anchor = noteAnchorOf(openMenuAnchor);
                  closeMenu();
                  notes.onRequest({ target: noteTarget, anchor, compose: true });
                }}
                className="flex w-full items-center gap-2 whitespace-nowrap border-gray-100 border-t px-3 py-2 text-left font-semibold text-[0.9em] hover:bg-gray-50" // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
              >
                Ajouter une note
              </button>
            )}
          </DrawerMenuPanel>
        )}
      </>
    );
  }

  /**
   * The marker of a spot with open discussions, or its « + » bubble when it has
   * none (never in global view, which creates nothing); `null` when the viewer
   * hid the markers or the grid carries no notes.
   */
  function renderNoteControl(target: NoteTarget, label: string): ReactNode {
    if (!notes || !notes.markersVisible) return null;
    const key = targetKey(target);
    const count = notes.counts.get(key) ?? 0;
    if (count > 0) {
      return (
        <NoteMarker
          count={count}
          targetLabel={label}
          latest={notes.latest.get(key)}
          onOpen={(anchor) => notes.onRequest({ target, anchor, compose: false })}
          {...(notes.now ? { now: notes.now } : {})}
        />
      );
    }
    if (globalView) return null;
    return (
      <NoteAddButton
        targetLabel={label}
        onAdd={(anchor) => notes.onRequest({ target, anchor, compose: true })}
      />
    );
  }

  // ── contentEditable raw-text editing (point 8) ─────────────────────────────
  // Makes the editor's initial text + caret placement mount-only, like the
  // old `<Textarea defaultValue={content} />` — so a background re-render
  // while the SAME cell stays in edit mode (e.g. a live matrix-regen refetch
  // invalidating `cells` mid-edit) never silently overwrites what the user
  // is typing.
  //
  // `editingContentRef`/`editingCellIdRef` hold the latest values for
  // whichever cell is currently being edited; they're written during render
  // (cheap, no re-render triggered) each time the loop below reaches that
  // cell, so `setEditorRef` — defined once via `useCallback([])` — always
  // reads fresh data without needing to close over it. This stability
  // matters: React calls a ref callback with `null` then the node again
  // whenever the ref prop's function identity changes, even without the DOM
  // node itself unmounting. An inline arrow function is a new identity on
  // every render, so a naive read-only-once-then-guard approach would still
  // get its "just unmounted" cleanup branch re-triggered on every unrelated
  // re-render, defeating the guard. A `useCallback([])`-stabilized ref only
  // fires on genuine mount/unmount.
  const editingContentRef = useRef<string>('');
  const editingCellIdRef = useRef<string | null>(null);
  const editorInitializedCellIdRef = useRef<string | null>(null);

  /**
   * Point 9 — Escape cancels an edit by clearing `editingCellId` (via
   * `onCancelEdit`) and letting the parent's re-render unmount the editor,
   * rather than reaching into the DOM to reset its text. But a focused
   * contentEditable being removed from the document can make the browser
   * fire a native `blur` on it as part of the unmount (focus has to go
   * somewhere), which would otherwise hit the `onBlur` handler below and
   * spuriously call `onSaveEdit` with the abandoned draft. This flag,
   * set right before `onCancelEdit` and cleared either by that same
   * `onBlur` or at the next edit session's mount, suppresses exactly that
   * one blur.
   */
  const suppressBlurSaveRef = useRef(false);

  /** The live editor node, for `focusEditorAtEnd` (point 4). Null when not editing. */
  const editorNodeRef = useRef<HTMLDivElement | null>(null);

  const setEditorRef = useCallback((node: HTMLDivElement | null) => {
    editorNodeRef.current = node;
    if (!node) {
      editorInitializedCellIdRef.current = null;
      return;
    }
    const cellId = editingCellIdRef.current;
    if (!cellId || editorInitializedCellIdRef.current === cellId) return;
    editorInitializedCellIdRef.current = cellId;
    suppressBlurSaveRef.current = false;
    node.textContent = editingContentRef.current;
    focusWithCaretAtEnd(node);
  }, []);

  /**
   * Point 4 — redirects a click that landed on the cell's padding into the
   * editor. Deliberately not implemented by stretching the editor to fill the
   * cell: `height: 100%` on a child of a <td> is unreliable across engines,
   * whereas refocusing is exact.
   */
  const focusEditorAtEnd = useCallback(() => {
    const node = editorNodeRef.current;
    if (node) focusWithCaretAtEnd(node);
  }, []);

  // ── Keyboard cell navigation (E14) ─────────────────────────────────────────
  const cellNodeRefs = useRef<Map<string, HTMLTableCellElement>>(new Map());

  function registerCellNode(cellId: string | undefined, node: HTMLTableCellElement | null) {
    if (!cellId) return;
    if (node) cellNodeRefs.current.set(cellId, node);
    else cellNodeRefs.current.delete(cellId);
  }

  // ── Rendered vs. navigable structure (M5/M15/E14) ──────────────────────────
  // Everything stays in the DOM — collapsing only swaps a column/row for a
  // sliver band. Arrow-key navigation therefore runs off separate "navigable"
  // lists, so a band is never a focus target.
  const orderedPaliers = [...paliers].sort(
    (a, b) => PALIER_NUMBERS.indexOf(a.number) - PALIER_NUMBERS.indexOf(b.number),
  );
  const isColumnCollapsed = (columnNumber: number) =>
    viewState.hiddenColumns.includes(columnNumber);
  const navigablePaliers = orderedPaliers.filter((palier) => !isColumnCollapsed(palier.number));
  const columnNavIndex = new Map(
    navigablePaliers.map((palier, index) => [palier.number, index] as const),
  );

  const activationRows: BodyRow[] = [...singularitySetup]
    .sort((a, b) => a.order - b.order)
    .map((levier, index) => ({
      kind: 'body' as const,
      rowType: 'activation' as const,
      rowRef: String(levier.order),
      label: levier.levierValeur ?? `Levier ${levier.order}`,
      isActivationHead: index === 0,
    }));

  const dimensionRows: RenderedRow[] = [...dimensions]
    .sort((a, b) => a.displayOrder - b.displayOrder)
    .map((dimension) =>
      viewState.hiddenDimensionIds.includes(dimension.id)
        ? { kind: 'dimension-collapsed' as const, rowRef: dimension.id, label: dimension.title }
        : {
            kind: 'body' as const,
            rowType: 'dimension' as const,
            rowRef: dimension.id,
            label: dimension.title,
            isActivationHead: false,
          },
    );

  const renderedRows: RenderedRow[] = [
    ...(viewState.activationRowCollapsed
      ? [{ kind: 'activation-collapsed' as const }]
      : activationRows),
    ...dimensionRows,
  ];
  const navigableRows = renderedRows.filter((row): row is BodyRow => row.kind === 'body');
  const rowNavIndex = new Map(
    navigableRows.map((row, index) => [rowHeightKey(row.rowType, row.rowRef), index] as const),
  );

  const cellMap = new Map<string, Cell>();
  for (const cell of cells) {
    cellMap.set(`${cell.rowType}:${cell.rowRef}:${cell.columnNumber}`, cell);
  }

  const multiSelectedIds = new Set(selection.scope === 'cells' ? selection.cellIds : []);
  /**
   * D25 — cells whose current version predates the matrix's last context
   * change. Absent prop means "no markers", so every pre-D25 call site is
   * unaffected; the route also passes an empty list once the banner is
   * dismissed (D26), which lands in the same no-marker branch.
   */
  const staleIds = new Set(staleCellIds ?? []);
  const columnCount = orderedPaliers.length;

  function moveSelection(rowIndex: number, columnIndex: number, direction: [number, number]) {
    const nextRow = navigableRows[rowIndex + direction[0]];
    const nextPalier = navigablePaliers[columnIndex + direction[1]];
    if (!nextRow || !nextPalier) return;
    const nextCell = cellMap.get(`${nextRow.rowType}:${nextRow.rowRef}:${nextPalier.number}`);
    if (!nextCell) return;
    // Global view moves focus without selecting (design doc R3): focus is what
    // opens the tooltip for keyboard users, selection has nowhere to lead.
    if (!globalView) onSelect({ scope: 'cell', cellId: nextCell.id });
    cellNodeRefs.current.get(nextCell.id)?.focus();
  }

  /**
   * The cell preview's header line — which cell this is (design doc P2).
   * `row.label` already resolves to the dimension title or the levier's
   * `levierValeur`, so both row kinds compose identically.
   */
  function cellPreviewHeader(row: BodyRow, palier: ResolvedPalier): string {
    return `${row.label} · Palier ${palierPosition(palier.number)} · ${palier.label}`;
  }

  /**
   * Global view only (design doc P5): a header or collapsed band is as
   * unreadable as the cells at the fit scale, so hovering it surfaces its full
   * name. These are plain strings, so the existing `ui/tooltip.tsx` covers them
   * — no need for `MatrixCellPreview`'s richer body.
   */
  function withLabelTooltip(key: string, label: string, node: ReactNode): ReactNode {
    if (!globalView) return node;
    return (
      <Tooltip key={key} content={label}>
        {node}
      </Tooltip>
    );
  }

  const zoomClass = ZOOM_TEXT_CLASSES[viewState.zoomStep];

  // ── Frame edges (point 1) ──────────────────────────────────────────────────
  // The scroll box hugs the table and opens its border on whichever edges
  // still have content behind them. Recomputed on scroll (the state is
  // scroll-position dependent, not just size dependent) and on any resize of
  // either the box or the table — collapsing a column, adding a dimension and
  // changing zoom all move content without resizing the box.
  const scrollBoxRef = useRef<HTMLDivElement | null>(null);
  const tableRef = useRef<HTMLTableElement | null>(null);
  const [frameEdges, setFrameEdges] = useState<ClippedEdges>(NO_CLIPPED_EDGES);
  /**
   * Global view's fit-to-screen factor (2026-08-25 design doc, V4). Measured in
   * the same `measure()` pass as the frame edges rather than by a second
   * observer, so a drawer opening, a window resize, an added dimension and a
   * collapsed row all refit for free. Stays at 1 in normal view.
   */
  const [fitScale, setFitScale] = useState(1);

  useEffect(() => {
    const box = scrollBoxRef.current;
    if (!box) return;

    function measure() {
      const node = scrollBoxRef.current;
      if (!node) return;
      const next = clippedEdges(node);
      setFrameEdges((current) => (sameClippedEdges(current, next) ? current : next));

      // Only global view applies the transform, so only global view needs the
      // factor. Guarding here keeps normal view's measure pass unchanged and
      // makes `globalView` a genuine dependency of this effect.
      const table = globalView ? tableRef.current : null;
      if (table) {
        setFitScale(
          computeFitScale({
            boxWidth: node.clientWidth,
            boxHeight: node.clientHeight,
            // `offsetWidth`/`offsetHeight` are the *unscaled* box: a transform
            // does not change layout size. That is exactly why measuring them
            // is stable across refits instead of feeding back on itself.
            tableWidth: table.offsetWidth,
            tableHeight: table.offsetHeight,
          }),
        );
      }
    }

    measure();
    box.addEventListener('scroll', measure, { passive: true });

    // ResizeObserver is unavailable in some test environments; the scroll
    // listener and the mount-time measure above still work without it.
    const observer =
      typeof ResizeObserver === 'function' ? new ResizeObserver(() => measure()) : null;
    observer?.observe(box);
    if (tableRef.current) observer?.observe(tableRef.current);

    return () => {
      box.removeEventListener('scroll', measure);
      observer?.disconnect();
    };
    // `globalView` re-runs the effect so entering the mode measures immediately
    // rather than waiting for the next scroll or resize event.
  }, [globalView]);

  return (
    // The forwarded ref lands on this outer column, not on the scroll box:
    // it is what the route uses to decide whether a click was "inside the
    // grid" (and so must not reset the selection), and the add-dimension
    // button below the frame has to count as inside.
    //
    // `items-start` stops the default stretch so the scroll box can be
    // narrower than the page when the table is; the box's own auto height,
    // plus `min-h-0` and a `shrink-0` button, make it exactly as tall as the
    // table until there is no room left, then scroll. That is the whole of
    // "the border follows the matrix" — see `clipped-edges.ts` for the edges.
    <div ref={ref} className="flex h-full min-h-0 flex-col items-start gap-3">
      <div
        ref={scrollBoxRef}
        data-testid="matrix-frame"
        className={cn(
          'min-h-0 max-w-full',
          // A scaled element still reserves its UNSCALED layout size, so an
          // `overflow-auto` box would render scrollbars around a visually tiny
          // table. Hiding the overflow also makes `clippedEdges` report nothing
          // clipped, which closes the frame's border on all four sides for free
          // (2026-08-25 design doc, V3).
          globalView ? 'overflow-hidden' : 'overflow-auto',
          clippedEdgeClasses(frameEdges),
          globalView ? GLOBAL_VIEW_TEXT_CLASS : zoomClass,
        )}
      >
        <table
          ref={tableRef}
          className="w-max border-collapse"
          style={
            globalView
              ? { transform: `scale(${fitScale})`, transformOrigin: 'top left' }
              : undefined
          }
        >
          <thead>
            <tr>
              {/* M3 — the corner cell is empty; the activation label lives on the first body row. */}
              <th
                data-testid="corner-header"
                colSpan={labelColumnCount}
                className={cn(
                  spanningLabelColumnClass,
                  !globalView && 'top-0',
                  'z-20 border-b-2 border-b-gray-200 bg-gray-50',
                )}
              />
              {orderedPaliers.map((palier) => {
                const columnRef = String(palier.number);

                // M5 — a collapsed column keeps its slot as a ~36px band. The
                // label stays visible, turned vertical (not just the re-expand
                // arrow), so a collapsed column is still identifiable at a
                // glance. Issue #16: the label replaced the palier number.
                // As in planche 06, the header row grows to fit the vertical
                // label; a label past 12rem (labels go up to 200 characters)
                // is clipped with an ellipsis, its full text in the tooltip.
                if (isColumnCollapsed(palier.number)) {
                  return withLabelTooltip(
                    `column-band-${palier.number}`,
                    `Palier ${palierPosition(palier.number)} · ${palier.label} (masquée)`,
                    <th
                      key={palier.number}
                      scope="col"
                      data-testid={`column-band-${palier.number}`}
                      className={cn(
                        headerRowPositionClass,
                        'z-10 w-9 min-w-9 border-gray-100 border-r border-b-2 border-b-gray-200 bg-gray-100 p-1 text-center align-middle',
                      )}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span
                          data-testid={`column-band-label-${palier.number}`}
                          title={palier.label}
                          className="inline-block max-h-48 rotate-180 overflow-hidden text-ellipsis whitespace-nowrap font-semibold text-gray-700 text-micro [writing-mode:vertical-rl]"
                        >
                          {palier.label}
                        </span>
                        {/* Re-expand writes shared `viewState`, so it is absent
                            in global view like every other visibility control
                            (design doc R4). */}
                        {!globalView && (
                          <button
                            type="button"
                            aria-label={`Afficher la colonne Palier ${palierPosition(palier.number)}`}
                            onClick={() => onToggleVisibility('column', columnRef, false)}
                            className="rounded p-0 text-gray-600 hover:bg-gray-200"
                          >
                            <Icon name="chevron_right" size={16} />
                          </button>
                        )}
                      </div>
                    </th>,
                  );
                }

                const isSelected =
                  selection.scope === 'column' && selection.columnNumber === palier.number;
                const width = columnWidthPx(palier.number);
                const isResizing = liveSize?.type === 'column' && liveSize.ref === columnRef;
                const columnNoteTarget: NoteTarget = {
                  type: 'column',
                  columnNumber: palier.number,
                };
                const columnNoteControl = renderNoteControl(
                  columnNoteTarget,
                  `Palier ${palier.label}`,
                );

                return withLabelTooltip(
                  `column-header-${palier.number}`,
                  `Palier ${palierPosition(palier.number)} · ${palier.label}`,
                  <th
                    key={palier.number}
                    scope="col"
                    data-testid={`column-header-${palier.number}`}
                    data-note-target={targetKey(columnNoteTarget)}
                    style={{ width, minWidth: width, ...headerStyle(palier.number) }}
                    className={cn(
                      'group z-10 border-gray-100 border-r border-b-2 border-b-gray-200 p-0 text-left align-top',
                      headerRowPositionClass,
                      headerBgClass(palier.number),
                      isSelected && 'ring-2 ring-inset',
                      // The gradient theme keeps its ramp colour when selected —
                      // flattening one column to a flat tint mid-ramp would break
                      // the progression the ramp exists to show, and an inline
                      // style would win over this class anyway. Ring only.
                      isSelected && !isGradientTheme && 'bg-bleu-nuit-tint',
                      isSelected && headerSelectionRingClass(palier.number),
                    )}
                  >
                    <div className={cn('relative flex items-start gap-1', cellPaddingClass)}>
                      {/* Nothing is selectable in global view (design doc R2),
                          so the label stops being a button rather than becoming
                          a control that does nothing when clicked. The name
                          stays reachable by keyboard through every cell's own
                          preview header line, which repeats it. */}
                      {/* Issue #16 (planche 06): no palier number in the header, the
                          label is the header's only text, bold at 1.1em. The palier's
                          position (issue #28) stays in the aria-label, the internal
                          number in the ids. The label inherits the
                          <th>'s colour (see headerStyle) so the noyau-gradient theme's
                          noir/blanc flip applies — no opacity: any alpha composites the
                          text against the header and would drop bleu-nuit p4 from
                          4.64:1 to ~3.38:1. */}
                      {/* Issue #19 (planche 10): the notes marker follows the label. */}
                      <div className="min-w-0 flex-1 text-left">
                        {globalView ? (
                          <span
                            // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                            className="block font-bold text-[1.1em] text-[color:inherit] leading-tight"
                          >
                            {' '}
                            {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                            {palier.label}
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() =>
                              onSelect({ scope: 'column', columnNumber: palier.number })
                            }
                            aria-pressed={isSelected}
                            aria-label={`Palier ${palierPosition(palier.number)} · ${palier.label}`}
                            className="focus-visible:-outline-offset-2 block w-full cursor-pointer text-left focus-visible:outline-2 focus-visible:outline-bleu-nuit"
                          >
                            <span
                              // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                              className="block font-bold text-[1.1em] text-[color:inherit] leading-tight"
                            >
                              {' '}
                              {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                              {palier.label}
                            </span>
                          </button>
                        )}
                        {columnNoteControl && <div className="mt-1">{columnNoteControl}</div>}
                      </div>
                      {/* M4 — hover collapse arrow, replacing the old "Masquer" menu item.
                          Absent in global view: it writes shared `viewState` (design doc R4). */}
                      {!globalView && (
                        <button
                          type="button"
                          aria-label={`Masquer la colonne Palier ${palierPosition(palier.number)}`}
                          onClick={() => onToggleVisibility('column', columnRef, true)}
                          className={HOVER_CONTROL_CLASS}
                        >
                          <Icon name="chevron_left" size={16} />
                        </button>
                      )}
                      {renderDrawerMenu(
                        `column:${columnRef}`,
                        `Options de la colonne Palier ${palierPosition(palier.number)}`,
                        { scope: 'column', columnNumber: palier.number },
                        'right',
                        columnNoteTarget,
                      )}
                      {/* No resizing in global view (design doc R4): it writes
                          shared `viewState`, and dragging a column in a fitted
                          view would only change the fit factor. */}
                      {!globalView && (
                        <div
                          role="separator"
                          tabIndex={0}
                          aria-orientation="vertical"
                          aria-label={`Redimensionner la colonne Palier ${palierPosition(palier.number)}`}
                          onPointerDown={(event) => beginColumnResize(event, columnRef, width)}
                          className={cn(
                            'absolute top-0 right-0 h-full w-1 cursor-col-resize select-none transition-colors hover:bg-bleu-nuit focus-visible:bg-bleu-nuit',
                            isResizing && 'bg-bleu-nuit',
                          )}
                        />
                      )}
                    </div>
                  </th>,
                );
              })}
            </tr>
          </thead>
          <tbody>
            {renderedRows.map((row) => {
              // ── M15 — the whole activation block, collapsed into one slim band.
              if (row.kind === 'activation-collapsed') {
                return (
                  <tr
                    key="activation-collapsed"
                    data-testid="activation-band"
                    className="group bg-gray-50"
                  >
                    {withLabelTooltip(
                      'activation-band',
                      `${ACTIVATION_ROW_LABEL} — ${ACTIVATION_ROW_COLLAPSED_HINT}`,
                      <th
                        scope="row"
                        colSpan={labelColumnCount}
                        className={cn(spanningLabelColumnClass, 'bg-gray-50 px-4 py-1 text-left')}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span
                            // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                            className="font-bold text-[0.9em] text-noir"
                          >
                            {' '}
                            {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                            {ACTIVATION_ROW_LABEL}
                          </span>
                          {/* Same control as the expanded state: right of the title, hover-only,
                          same icon — the row never grows a second, differently-placed arrow.
                          Absent in global view, like every other visibility write (R4): the
                          band stays a band there, and "Tout afficher" is the live path. */}
                          {!globalView && (
                            <button
                              type="button"
                              aria-label={`Déplier ${ACTIVATION_ROW_LABEL}`}
                              onClick={() => onToggleVisibility('activation', null, false)}
                              className={HOVER_CONTROL_CLASS}
                            >
                              <Icon name="expand_more" size={16} />
                            </button>
                          )}
                        </div>
                      </th>,
                    )}
                    <td
                      colSpan={columnCount}
                      className="border-gray-100 border-b bg-gray-50 px-4 py-1"
                    >
                      <span
                        // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                        className="font-light text-[0.85em] text-gray-700"
                      >
                        {' '}
                        {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                        {ACTIVATION_ROW_COLLAPSED_HINT}
                      </span>
                    </td>
                  </tr>
                );
              }

              // ── M5 — a collapsed dimension row, still re-expandable on its own.
              if (row.kind === 'dimension-collapsed') {
                return (
                  <tr
                    key={`dimension-collapsed:${row.rowRef}`}
                    data-testid={`dimension-band-${row.rowRef}`}
                    className="group bg-gray-100"
                  >
                    {withLabelTooltip(
                      `dimension-band-${row.rowRef}`,
                      `${row.label} (masquée)`,
                      <th
                        scope="row"
                        colSpan={labelColumnCount}
                        className={cn(spanningLabelColumnClass, 'bg-gray-100 px-4 py-1 text-left')}
                      >
                        <div className="flex items-center justify-between gap-1">
                          <span
                            // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                            className="font-semibold text-[0.85em] text-gray-700"
                          >
                            {' '}
                            {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                            {row.label} (masquée)
                          </span>
                          {/* Same control as the expanded row: right of the title, hover-only,
                            same icon as the "hide" trigger — no left/right or up/down swap. */}
                          {!globalView && (
                            <button
                              type="button"
                              aria-label={`Afficher la ligne ${row.label}`}
                              onClick={() => onToggleVisibility('dimension', row.rowRef, false)}
                              className={HOVER_CONTROL_CLASS}
                            >
                              <Icon name="expand_more" size={16} />
                            </button>
                          )}
                        </div>
                      </th>,
                    )}
                    <td
                      colSpan={columnCount}
                      className="border-gray-100 border-b bg-gray-100 px-4 py-1"
                    />
                  </tr>
                );
              }

              const isRowSelected =
                selection.scope === 'row' &&
                selection.rowType === row.rowType &&
                selection.rowRef === row.rowRef;
              const height = rowHeightPx(row.rowType, row.rowRef);
              const heightKey = rowHeightKey(row.rowType, row.rowRef);
              const rowIndex = rowNavIndex.get(heightKey) ?? -1;
              const isResizingRow = liveSize?.type === 'row' && liveSize.ref === heightKey;

              // M3 (session 5) — the 6 activation sub-rows share ONE header cell
              // (rowSpan), showing only "Ligne d'activation" — no per-levier name,
              // no per-row select/resize/"..." affordances (there's nowhere left to
              // anchor them once the header no longer distinguishes rows). Dimension
              // rows are untouched: one header per row, exactly as before.
              const isActivationSubRow = row.rowType === 'activation';
              const rowNoteTarget: NoteTarget = {
                type: 'row',
                rowType: row.rowType,
                rowRef: row.rowRef,
              };
              const rowNoteControl = isActivationSubRow
                ? null
                : renderNoteControl(rowNoteTarget, `Ligne ${row.label}`);

              // Point 14 — the add-dimension job matching THIS row, if any. The
              // dimension row itself already exists (created eagerly by the
              // backend before the job even starts), so only its 8 cells are
              // ever missing while a job is pending/running/failed.
              const dimensionRowJob: DimensionGenerationJob | null =
                row.rowType === 'dimension' &&
                dimensionJob !== null &&
                dimensionJob.dimensionId === row.rowRef
                  ? dimensionJob
                  : null;
              const isPendingDimensionRow =
                dimensionRowJob?.status === 'pending' || dimensionRowJob?.status === 'running';

              // Failed job: the row's 8 individual cells never materialized, so
              // there's nothing meaningful to render per-column — one spanning
              // error message + retry action replaces the usual per-palier
              // `<td>` cells instead.
              const bodyCells: ReactNode =
                dimensionRowJob && dimensionRowJob.status === 'failed'
                  ? (() => {
                      const job = dimensionRowJob;
                      return (
                        <td
                          colSpan={columnCount}
                          className="border-gray-100 border-b bg-white px-4 py-3 align-top"
                        >
                          <div className="space-y-2 text-danger text-small">
                            <p>
                              La génération de cette dimension a échoué. Réessayez, ou contactez un
                              administrateur si le problème persiste.
                            </p>
                            {job.error && (
                              <details className="text-caption text-gray-700">
                                <summary className="cursor-pointer">Détails techniques</summary>
                                <p className="mt-1 whitespace-pre-wrap font-mono">{job.error}</p>
                              </details>
                            )}
                            <Button
                              type="button"
                              size="sm"
                              variant="primary"
                              disabled={isRetryingDimensionJob}
                              onClick={() => onRetryDimensionJob(job.id)}
                            >
                              Réessayer
                            </Button>
                          </div>
                        </td>
                      );
                    })()
                  : orderedPaliers.map((palier) => {
                      // M5 — the body slice of a collapsed column: a narrow gray sliver,
                      // deliberately not focusable and carrying no cell affordances.
                      if (isColumnCollapsed(palier.number)) {
                        return (
                          <td
                            key={palier.number}
                            className="w-9 min-w-9 border-gray-100 border-r border-b bg-gray-100"
                          />
                        );
                      }

                      const columnIndex = columnNavIndex.get(palier.number) ?? -1;
                      const cell = cellMap.get(`${row.rowType}:${row.rowRef}:${palier.number}`);
                      const isSelected =
                        selection.scope === 'cell' &&
                        cell !== undefined &&
                        selection.cellId === cell.id;
                      const isMultiSelected = cell !== undefined && multiSelectedIds.has(cell.id);
                      const isEditing = cell !== undefined && editingCellId === cell.id;
                      const content = currentContent(cell);
                      const width = columnWidthPx(palier.number);
                      const hasRegenerationBadge =
                        cell !== undefined && recentlyRegeneratedCellIds.has(cell.id);
                      // D25 — this cell's current version predates the last
                      // context change. Purely informative: unlike the
                      // regeneration badge it is *not* cleared on hover, since
                      // only the banner's dismiss control (D26) retires it.
                      const isContextStale = cell !== undefined && staleIds.has(cell.id);
                      // Point 14 — this specific cell hasn't materialized yet
                      // because its dimension's add-dimension job is still
                      // pending/running (distinct from simply having no content).
                      const isPendingPlaceholder = !cell && isPendingDimensionRow;
                      const cellNoteTarget: NoteTarget = {
                        type: 'cell',
                        rowType: row.rowType,
                        rowRef: row.rowRef,
                        columnNumber: palier.number,
                      };
                      const cellNoteControl = cell
                        ? renderNoteControl(cellNoteTarget, `${row.label} × ${palier.label}`)
                        : null;
                      const cellOpenNoteCount =
                        notes?.markersVisible === true
                          ? (notes.counts.get(targetKey(cellNoteTarget)) ?? 0)
                          : 0;

                      if (isEditing && cell) {
                        // Latest values for the stable `setEditorRef` callback (see
                        // above) to read at mount time — see that declaration for why
                        // this can't just be a closure captured here instead.
                        editingCellIdRef.current = cell.id;
                        editingContentRef.current = content;
                      }

                      /**
                       * M11 — in multi-select mode a click only adds/removes the cell
                       * from the set; there is no reclick-to-edit path at all. Outside
                       * it, behaviour is unchanged (D10).
                       */
                      function activateCell() {
                        if (!cell) return;
                        // Global view is read-only: no selection and no
                        // click-to-edit (2026-08-25 design doc, R1/R2). Cells
                        // stay focusable — see `tabIndex` below — so the hover
                        // tooltip is keyboard-reachable too.
                        if (globalView) return;
                        // Point 4 — while this cell is already being edited, a
                        // click landing on the <td> itself (i.e. inside the cell
                        // but below the text) must not re-enter edit mode. It
                        // used to call `onStartEdit` again for the very same
                        // physical click that had just blurred-and-saved the
                        // editor, remounting it against pre-save content and
                        // producing a second, spurious cell_versions row. The
                        // click is instead redirected into the editor, caret at
                        // the end, so the whole cell behaves as one text surface.
                        if (isEditing) {
                          focusEditorAtEnd();
                          return;
                        }
                        if (multiSelectMode) {
                          onSelect(toggleCellInSelection(selection, cell.id));
                          return;
                        }
                        if (isSelected) {
                          onStartEdit(cell.id);
                        } else {
                          onSelect({ scope: 'cell', cellId: cell.id });
                        }
                      }

                      const cellNode = (
                        <td
                          key={palier.number}
                          ref={(node) => registerCellNode(cell?.id, node)}
                          data-testid={`cell-${row.rowType}-${row.rowRef}-${palier.number}`}
                          data-note-target={cell ? targetKey(cellNoteTarget) : undefined}
                          tabIndex={cell ? 0 : undefined}
                          style={{ width, minWidth: width, height, minHeight: height }}
                          onClick={activateCell}
                          onMouseEnter={() => {
                            // Not in global view: with a zero-delay tooltip on
                            // every cell, one mouse sweep across a fitted grid
                            // would silently clear every regeneration marker on
                            // the matrix. Looking must not destroy state, even
                            // ephemeral per-viewer state (design doc R6).
                            if (globalView) return;
                            if (cell && hasRegenerationBadge) onDismissRegenerationBadge(cell.id);
                          }}
                          onKeyDown={(event) => {
                            if (!cell) return;
                            if (isEditing) {
                              // Point 9 — while editing, the table's own keyboard
                              // handling is fully inert: arrows move the text
                              // caret natively, Enter/Space type normally. Only
                              // Escape gets new behavior here (cancel + revert,
                              // via `onCancelEdit` unmounting the editor — see
                              // `suppressBlurSaveRef` above); Tab is untouched
                              // (falls through, native browser focus-move
                              // behavior applies, same as today).
                              if (event.key === 'Escape') {
                                event.preventDefault();
                                suppressBlurSaveRef.current = true;
                                onCancelEdit(cell.id);
                              }
                              return;
                            }
                            switch (event.key) {
                              case 'ArrowUp':
                                event.preventDefault();
                                moveSelection(rowIndex, columnIndex, [-1, 0]);
                                return;
                              case 'ArrowDown':
                                event.preventDefault();
                                moveSelection(rowIndex, columnIndex, [1, 0]);
                                return;
                              case 'ArrowLeft':
                                event.preventDefault();
                                moveSelection(rowIndex, columnIndex, [0, -1]);
                                return;
                              case 'ArrowRight':
                                event.preventDefault();
                                moveSelection(rowIndex, columnIndex, [0, 1]);
                                return;
                              default:
                                handleActivationKey(event, activateCell);
                            }
                          }}
                          className={cn(
                            // M16 — focus is a plain outline; selection is an inset ring + tint.
                            'group focus-visible:-outline-offset-2 relative border-gray-100 border-r border-b bg-white align-top focus-visible:outline-2 focus-visible:outline-bleu-nuit',
                            // Read-only in global view, so the cursor must not
                            // promise a click that does nothing (design doc R7).
                            globalView ? 'cursor-default' : 'cursor-pointer',
                            cellPaddingClass,
                            !cell && 'cursor-default bg-gray-50',
                            // Nothing is selectable in global view, so neither
                            // ring can ever apply there.
                            !globalView &&
                              isSelected &&
                              'bg-bleu-nuit-tint ring-2 ring-bleu-nuit ring-inset',
                            !globalView &&
                              isMultiSelected &&
                              'bg-rose-tint ring-2 ring-rose ring-inset',
                          )}
                        >
                          {isEditing && cell ? (
                            // biome-ignore lint/a11y/useKeyWithClickEvents: onClick only stops a caret-repositioning click from bubbling to the <td>'s activateCell handler (point 9); it triggers no action of its own — real editing goes through native contentEditable keyboard input.
                            <div
                              // Point 4 — this `key` is load-bearing, not
                              // cosmetic. Without it, this <div> and the display
                              // <div> below are the same element type in the same
                              // slot, so React reuses ONE host node between them
                              // instead of unmounting. Since the editor's text is
                              // written imperatively (`node.textContent`, see
                              // `setEditorRef`), React never owns it — so on the
                              // swap back to display mode it appended the
                              // <p>/<ul> children *beside* the surviving raw text
                              // node and the cell rendered its content twice, for
                              // good. Distinct keys force a real unmount/remount.
                              key="editor"
                              data-testid="cell-editor"
                              // `plaintext-only`, not plain `contentEditable`: it
                              // makes the browser convert a pasted rich/multi-line
                              // selection to plain text on the way in, so no block
                              // elements ever land in here (see
                              // `insertPlainTextAtCaret` for why that matters and
                              // for the Firefox fallback).
                              contentEditable="plaintext-only"
                              suppressContentEditableWarning
                              role="textbox"
                              tabIndex={0}
                              aria-multiline="true"
                              // `whitespace-pre-wrap` is load-bearing, not
                              // cosmetic: it is what makes the browser insert a
                              // literal "\n" character for a manual Enter instead
                              // of a <br>/<div>, which is the only shape the
                              // `textContent` read below round-trips faithfully.
                              // Removing it silently collapses multi-line cells
                              // into one line on save.
                              className="whitespace-pre-wrap text-[0.95em] leading-relaxed outline-none" // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                              ref={setEditorRef}
                              onClick={(event) => event.stopPropagation()}
                              onPaste={(event) => {
                                // Never let the browser's own (potentially rich)
                                // insertion run — normalize to plain text.
                                event.preventDefault();
                                const text = event.clipboardData.getData('text/plain');
                                if (text.length === 0) return;
                                insertPlainTextAtCaret(text);
                              }}
                              onBlur={(event) => {
                                if (suppressBlurSaveRef.current) {
                                  suppressBlurSaveRef.current = false;
                                  return;
                                }
                                const nextValue = event.currentTarget.textContent ?? '';
                                if (nextValue !== content) {
                                  onSaveEdit(cell.id, nextValue);
                                }
                              }}
                            />
                          ) : content.length > 0 ? (
                            <div
                              key="display"
                              // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                              className="space-y-1 text-[0.95em] leading-relaxed"
                            >
                              {' '}
                              {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                              {parseBulletLines(content).map((line, index) =>
                                line.hasBullet ? (
                                  // biome-ignore lint/suspicious/noArrayIndexKey: bullet lines have no stable identity
                                  <ul key={index} className="list-inside list-disc">
                                    <li>{line.text}</li>
                                  </ul>
                                ) : (
                                  // biome-ignore lint/suspicious/noArrayIndexKey: bullet lines have no stable identity
                                  <p key={index}>{line.text}</p>
                                ),
                              )}
                            </div>
                          ) : isPendingPlaceholder ? (
                            // Point 14 — a loading skeleton instead of "Aucun contenu"
                            // while this dimension's add-dimension job is still running.
                            <div
                              key="pending"
                              data-testid={`cell-pending-${row.rowType}-${row.rowRef}-${palier.number}`}
                              aria-hidden="true"
                              className="space-y-1"
                            >
                              <span className="block h-2.5 w-4/5 animate-pulse rounded bg-gray-100" />
                              <span className="block h-2.5 w-3/5 animate-pulse rounded bg-gray-100" />
                            </div>
                          ) : (
                            <span
                              // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                              className="text-[0.85em] text-gray-700 italic"
                            >
                              {' '}
                              {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                              Aucun contenu
                            </span>
                          )}
                          {/* 2026-08-14 design doc, I7/I8 — regenerated-but-unselected marker, clears on hover. */}
                          {hasRegenerationBadge && (
                            <span
                              aria-hidden="true"
                              title="Régénérée récemment"
                              className="absolute top-1 left-1 h-2 w-2 rounded-full bg-vert"
                            />
                          )}
                          {/*
                            2026-08-26 design doc, D25 — "marqueur discret" on a
                            cell that predates the last context change. A hint,
                            not an alarm: it is a 6px dot in the bottom-left
                            gutter, absolutely positioned so it never shifts the
                            cell's text, and it deliberately stays out of the
                            three corners already spoken for (regeneration badge
                            top-left, drawer menu top-right). It tints nothing,
                            so the palier gradient and the selection/multi-select
                            rings keep their full attention weight.

                            `role="img"` + `aria-label` (rather than the
                            regeneration badge's `aria-hidden` + `title`) because
                            this marker is the *only* per-cell carrier of the
                            information: the banner it belongs to gives a count,
                            not a list, so a colour-only dot would leave a
                            screen-reader user with no way to tell which cells
                            are concerned.
                          */}
                          {isContextStale && (
                            <span
                              role="img"
                              data-testid={`cell-stale-${row.rowType}-${row.rowRef}-${palier.number}`}
                              aria-label={CONTEXT_STALE_MARKER_LABEL}
                              title={CONTEXT_STALE_MARKER_LABEL}
                              className="absolute bottom-1 left-1 h-1.5 w-1.5 rounded-full bg-warning"
                            />
                          )}
                          {/* M7 — the same drawer shortcut as the headers, on every body cell. */}
                          {cell && (
                            <div className="absolute top-1 right-1">
                              {renderDrawerMenu(
                                `cell:${cell.id}`,
                                `Options de la cellule ${row.label} · Palier ${palierPosition(palier.number)}`,
                                { scope: 'cell', cellId: cell.id },
                                'right',
                                cellNoteTarget,
                              )}
                            </div>
                          )}
                          {/* Issue #19 (planche 10): bottom-right, the only corner left free. */}
                          {cellNoteControl && (
                            <div className="absolute right-1 bottom-1">{cellNoteControl}</div>
                          )}
                        </td>
                      );

                      // Global view only (design doc P6): the cell itself is
                      // unreadable at the fit scale, so hover/focus surfaces a
                      // legible copy. `Trigger asChild` forwards onto the <td>,
                      // so its ref, tabIndex, testid, style and handlers all
                      // survive the wrap untouched.
                      return globalView ? (
                        <MatrixCellPreview
                          key={palier.number}
                          header={cellPreviewHeader(row, palier)}
                          content={content}
                          isPending={isPendingPlaceholder}
                          openNoteCount={cellOpenNoteCount}
                        >
                          {cellNode}
                        </MatrixCellPreview>
                      ) : (
                        cellNode
                      );
                    });

              return (
                <tr key={heightKey}>
                  {(!isActivationSubRow || row.isActivationHead) &&
                    withLabelTooltip(
                      `row-header-${heightKey}`,
                      isActivationSubRow ? ACTIVATION_ROW_LABEL : row.label,
                      <th
                        scope="row"
                        data-testid={isActivationSubRow ? 'row-header-activation' : undefined}
                        data-note-target={isActivationSubRow ? undefined : targetKey(rowNoteTarget)}
                        rowSpan={isActivationSubRow ? activationRows.length : undefined}
                        // A dimension row has no levier name, so it spans the
                        // whole label block whenever the name column exists.
                        colSpan={isActivationSubRow ? undefined : labelColumnCount}
                        style={isActivationSubRow ? undefined : { height, minHeight: height }}
                        className={cn(
                          isActivationSubRow ? groupColumnClass : spanningLabelColumnClass,
                          'group bg-gray-50 p-0 text-left align-top',
                          isRowSelected &&
                            !isActivationSubRow &&
                            'bg-bleu-nuit-tint ring-2 ring-bleu-nuit ring-inset',
                        )}
                      >
                        {isActivationSubRow ? (
                          <div
                            className={cn(
                              'relative flex h-full items-center gap-1',
                              // In strip mode the cell is only 28px wide: the
                              // title is gone (it moves to the name column's
                              // group header text) and only the two controls
                              // remain, stacked so they fit.
                              isStripMode
                                ? 'flex-col justify-start px-0 py-1'
                                : cn('justify-between', cellPaddingClass),
                            )}
                          >
                            {levierColorTreatment === 'bar' && (
                              /* Six-segment colour band down the left edge of the
                               block. Segment heights reuse `rowHeightPx` — the
                               exact values the body cells are set to — so the
                               bands line up with their rows. A row whose
                               content overflows that height will drift, the
                               same way its own body cells do. */
                              <div
                                aria-hidden="true"
                                data-testid="activation-levier-band"
                                className="absolute top-0 left-0 flex w-1.5 flex-col overflow-hidden"
                              >
                                {activationRows.map((activationRow) => (
                                  <span
                                    key={activationRow.rowRef}
                                    style={{
                                      height: rowHeightPx('activation', activationRow.rowRef),
                                    }}
                                    className={cn(
                                      'w-full flex-shrink-0',
                                      LEVIER_SOLID_CLASSES[
                                        levierColorToken(Number(activationRow.rowRef))
                                      ],
                                    )}
                                  />
                                ))}
                              </div>
                            )}
                            {!isStripMode && (
                              <span
                                // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                                className="font-bold text-[0.95em] text-noir"
                              >
                                {' '}
                                {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                                {ACTIVATION_ROW_LABEL}
                              </span>
                            )}
                            <div
                              className={cn('flex items-center gap-1', isStripMode && 'flex-col')}
                            >
                              {/* Same position/icon as every other row's collapse control —
                              right of the title, hover-only, never swaps direction. */}
                              {!globalView && (
                                <button
                                  type="button"
                                  aria-label={`Replier ${ACTIVATION_ROW_LABEL}`}
                                  onClick={() => onToggleVisibility('activation', null, true)}
                                  className={HOVER_CONTROL_CLASS}
                                >
                                  <Icon name="expand_less" size={16} />
                                </button>
                              )}
                              {/* Task 4 — the block-level "..." menu: only "Régénérer avec
                              commentaire" (scoped to `activationBlock`) and "Historique",
                              no per-levier item (grill decision — the header no longer
                              distinguishes rows, session 5, M3). */}
                              {renderDrawerMenu(
                                'activation-block',
                                'Plus d’options',
                                { scope: 'activationBlock' },
                                'left',
                              )}
                            </div>
                            {isStripMode && (
                              /* The group label still has to be readable when the
                               cell is only 28px wide, so it turns on its side
                               rather than disappearing. `vertical-rl` + 180°
                               reads bottom-to-top, the conventional direction
                               for a rotated table label. */
                              <span
                                // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                                className="flex-1 rotate-180 whitespace-nowrap font-bold text-[0.8em] text-noir [writing-mode:vertical-rl]"
                              >
                                {' '}
                                {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                                {ACTIVATION_ROW_LABEL}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div
                            className={cn(
                              'relative flex h-full items-start gap-1',
                              cellPaddingClass,
                            )}
                          >
                            {/* Same as the column header: a label, not a control,
                              once selection is off (design doc R2). */}
                            {/* Issue #19 (planche 10): the notes marker follows the label. */}
                            <div className="min-w-0 flex-1 text-left">
                              {globalView ? (
                                <span
                                  // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                                  className="block font-semibold text-[0.95em] text-noir"
                                >
                                  {' '}
                                  {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                                  {row.label}
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() =>
                                    onSelect({
                                      scope: 'row',
                                      rowType: row.rowType,
                                      rowRef: row.rowRef,
                                    })
                                  }
                                  aria-pressed={isRowSelected}
                                  className="focus-visible:-outline-offset-2 block w-full cursor-pointer text-left font-semibold text-[0.95em] text-noir focus-visible:outline-2 focus-visible:outline-bleu-nuit" // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                                >
                                  {row.label}
                                </button>
                              )}
                              {rowNoteControl && <div className="mt-1">{rowNoteControl}</div>}
                            </div>
                            {!globalView && (
                              <button
                                type="button"
                                aria-label={`Masquer la ligne ${row.label}`}
                                onClick={() => onToggleVisibility('dimension', row.rowRef, true)}
                                className={HOVER_CONTROL_CLASS}
                              >
                                <Icon name="expand_less" size={16} />
                              </button>
                            )}
                            {renderDrawerMenu(
                              `row:${heightKey}`,
                              `Options de la ligne ${row.label}`,
                              { scope: 'row', rowType: row.rowType, rowRef: row.rowRef },
                              'left',
                              rowNoteTarget,
                            )}
                            {!globalView && (
                              <div
                                role="separator"
                                tabIndex={0}
                                aria-orientation="horizontal"
                                aria-label={`Redimensionner la ligne ${row.label}`}
                                onPointerDown={(event) => beginRowResize(event, heightKey, height)}
                                className={cn(
                                  'absolute bottom-0 left-0 h-1 w-full cursor-row-resize select-none transition-colors hover:bg-bleu-nuit focus-visible:bg-bleu-nuit',
                                  isResizingRow && 'bg-bleu-nuit',
                                )}
                              />
                            )}
                          </div>
                        )}
                      </th>,
                    )}
                  {/* Point 7 — the per-levier name column. Present on every
                      activation sub-row (not rowSpan'd) whenever the
                      `matrix-activation-levier-names` tweak asks for names;
                      dimension rows colSpan over it instead. This is also the
                      only surface `accent`/`tint` can colour, which is why
                      those two fall back to the band when it is absent. */}
                  {isActivationSubRow && hasNameColumn && (
                    <th
                      scope="row"
                      data-testid={`row-header-levier-${row.rowRef}`}
                      style={{ height, minHeight: height }}
                      className={cn(
                        nameColumnClass,
                        'bg-gray-50 text-left align-top',
                        cellPaddingClass,
                        levierColorTreatment === 'tint' &&
                          LEVIER_TINT_CLASSES[levierColorToken(Number(row.rowRef))],
                        levierColorTreatment === 'accent' &&
                          cn(
                            'border-l-4',
                            LEVIER_BORDER_CLASSES[levierColorToken(Number(row.rowRef))],
                          ),
                      )}
                    >
                      <span
                        // pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice
                        className="flex items-center gap-1 font-semibold text-[0.9em] text-noir"
                      >
                        {' '}
                        {/* pms-design-exception: taille en em : le texte de la grille suit le zoom de la matrice */}
                        {levierColorTreatment === 'accent' && (
                          <span
                            aria-hidden="true"
                            className={cn(
                              'h-2 w-2 flex-shrink-0 rounded-full',
                              LEVIER_SOLID_CLASSES[levierColorToken(Number(row.rowRef))],
                            )}
                          />
                        )}
                        {row.label}
                      </span>
                    </th>
                  )}
                  {bodyCells}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Point 14 / 2026-08-24 grill point 5 — outside the frame now, left-
          aligned to it. It used to be the table's own last <tr>, which put it
          inside the border and, on a tall matrix, out of reach until you had
          scrolled to the bottom. */}
      {/* Hidden in global view (design doc F3): it is an editing affordance,
          and being a `shrink-0` block below the frame it also costs vertical
          room the fit could otherwise use. */}
      {!globalView && (
        <div className="shrink-0 pb-4">
          <AddDimensionButton
            onSubmit={onAddDimension}
            isSubmitting={isAddingDimension}
            submitError={addDimensionError}
          />
        </div>
      )}
    </div>
  );
});
